// UI生成コンポーネント

import { t, getSettingTitle, getSettingDescription, getCategoryTitle, getSectionTitle, getSettingCode, getLocalizedText } from '../utils/i18n.js';
import type { SettingItem, Category, Section } from '../types/index.js';

/**
 * NIC設定の動的リストUIを生成
 * @param item - 設定項目
 * @returns HTML文字列
 */
function createNICConfigHTML(item: SettingItem): string {
    const adminBadge = item.requiresAdmin ? `<span class="text-xs bg-red-600 text-white px-2 py-0.5 rounded mr-2" title="${t('adminBadgeTitle')}">${t('adminBadge')}</span>` : '';
    const docsButton = `
        <button type="button"
            class="text-[10px] px-2 py-0.5 rounded border border-gray-700 text-gray-300 hover:bg-gray-800 hover:text-white transition ml-2"
            title="${t('docsButtonTitle')}"
            onclick="window.app.openDocs('${item.id}')">
            ${t('docsButton')}
        </button>
    `;
    
    return `
        <div class="bg-gray-800 p-2 sm:p-3 md:p-4 rounded-lg mb-2 sm:mb-3 shadow-sm border border-gray-700 hover:border-gray-500 transition" id="toggleContainer_${item.id}">
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 gap-2">
                <div class="flex items-center gap-2">
                    <input type="checkbox" name="toggle" id="${item.id}" class="toggle-checkbox w-6 h-6 rounded border-gray-600 bg-gray-700 text-blue-600 focus:ring-blue-500" onchange="window.app.updateCode()"/>
                    <div class="flex flex-wrap items-center gap-2">
                        <h3 class="font-bold text-xs sm:text-sm">${getSettingTitle(item.id, item.title)}</h3>
                        ${adminBadge}
                        ${docsButton}
                    </div>
                </div>
            </div>
            <p class="text-xs text-gray-400 mb-3">${getSettingDescription(item.id, item.description)}</p>
            <div id="nicList_${item.id}" class="space-y-3">
                <!-- NIC設定がここに動的に追加されます -->
            </div>
            <button 
                type="button"
                onclick="window.app.addNIC('${item.id}')"
                class="mt-3 w-full px-3 py-2 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded transition">
                ${t('addNIC')}
            </button>
            <div id="codePreview_${item.id}" class="mt-2 text-[11px] text-green-300 bg-black/40 rounded px-2 py-1 font-mono overflow-x-auto break-words hidden">
                <span class="text-gray-500 pr-1">${t('codePreview')}</span>
                <pre class="inline whitespace-pre-wrap align-middle break-all"></pre>
            </div>
        </div>
    `;
}

/**
 * トグルスイッチのHTMLを生成
 * @param item - 設定項目
 * @returns HTML文字列
 */
export function createToggleHTML(item: SettingItem): string {
    // 管理者権限チェック項目の場合のみ警告アイコンを追加
    const warningIcon = item.isAdminCheck ? `<span id="adminWarningIcon" class="hidden text-yellow-400 text-lg mr-2" title="${t('adminWarningTitle')}">⚠️</span>` : '';
    // 管理者権限が必要な設定にはマークを表示
    const adminBadge = item.requiresAdmin ? `<span class="text-xs bg-red-600 text-white px-2 py-0.5 rounded mr-2" title="${t('adminBadgeTitle')}">${t('adminBadge')}</span>` : '';
    // 再起動が必要な設定にはマークを表示
    const rebootBadge = (item as any).requiresReboot ? `<span class="text-xs bg-orange-600 text-white px-2 py-0.5 rounded mr-2" title="${t('rebootBadgeTitle')}">${t('rebootBadge')}</span>` : '';
    // 元に戻すコードが定義されている設定にはマークを表示
    const revertBadge = item.revertCode ? `<span class="text-xs bg-blue-600 text-white px-2 py-0.5 rounded mr-2" title="${t('revertBadgeTitle')}">${t('revertBadge')}</span>` : '';
    // Docsボタン（SEO用ドロワーを開く）
    const docsButton = `
        <button type="button"
            class="text-[10px] px-2 py-0.5 rounded border border-gray-700 text-gray-300 hover:bg-gray-800 hover:text-white transition ml-2"
            title="${t('docsButtonTitle')}"
            onclick="window.app.openDocs('${item.id}')">
            ${t('docsButton')}
        </button>
    `;
    // 動的リスト（NIC設定など）の場合は特別なUIを生成
    if ((item as any).isDynamicList && (item as any).listItemType === 'nic') {
        return createNICConfigHTML(item);
    }
    
    // 変数入力欄を生成
    let variablesHTML = '';
    if (item.variables && item.variables.length > 0) {
        const variablesList = item.variables.map(variable => {
            const inputType = variable.type === 'password' ? 'password' : 'text';
            const storageKey = `var_${item.id}_${variable.name}`;
            // パスワードフィールドの場合は、Chromeのパスワードマネージャーを無効化
            const autocompleteAttr = inputType === 'password' 
                ? 'autocomplete="new-password" data-lpignore="true" data-form-type="other"'
                : 'autocomplete="off"';
            
            return `
                <div class="mb-2">
                    <label class="block text-[10px] text-gray-400 mb-1">${getLocalizedText(variable.label)}</label>
                    <input 
                        type="${inputType}"
                        id="${storageKey}"
                        data-item-id="${item.id}"
                        data-var-name="${variable.name}"
                        class="w-full px-2 py-1 text-xs bg-gray-900 border border-gray-600 rounded text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                        placeholder="${(variable as any).placeholder || variable.defaultValue || ''}"
                        value="${variable.defaultValue || ''}"
                        ${autocompleteAttr}
                        oninput="window.app.saveVariableValue('${item.id}', '${variable.name}', this.value); window.app.updateCodePreview('${item.id}'); window.app.updateCode(false)"
                        onchange="window.app.saveVariableValue('${item.id}', '${variable.name}', this.value)"
                    />
                </div>
            `;
        }).join('');
        variablesHTML = `
            <div class="mt-2 p-2 bg-gray-900/50 rounded border border-gray-700">
                <div class="text-[10px] text-gray-400 mb-2">${t('variableSettings')}</div>
                ${variablesList}
            </div>
        `;
    }
    
    // この項目が追加するバッチコード全文をプレビュー（空ならメッセージ）
    let codePreview = '';
    const translatedCode = getSettingCode(item.id, item.code || '');
    if (translatedCode && translatedCode.trim().length > 0) {
        const safeCode = translatedCode
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');
        codePreview = `
            <div id="codePreview_${item.id}" class="mt-2 text-[11px] text-green-300 bg-black/40 rounded px-2 py-1 font-mono overflow-x-auto break-words">
                <span class="text-gray-500 pr-1">${t('codePreview')}</span>
                <pre class="inline whitespace-pre-wrap align-middle break-all">${safeCode}</pre>
            </div>
        `;
    } else {
        codePreview = `
            <div class="mt-2 text-[11px] text-gray-500 bg-black/20 rounded px-2 py-1 font-mono">
                ${t('codePreviewEmpty')}
            </div>
        `;
    }
    return `
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between bg-gray-800 p-2 sm:p-3 md:p-4 rounded-lg mb-2 sm:mb-3 shadow-sm border border-gray-700 hover:border-gray-500 transition" id="toggleContainer_${item.id}">
            <div class="flex items-center flex-1 mb-3 sm:mb-0">
                ${warningIcon}
                <div class="flex-1">
                    <div class="flex flex-wrap items-center gap-2 mb-1">
                        <h3 class="font-bold text-xs sm:text-sm">${getSettingTitle(item.id, item.title)}</h3>
                        ${adminBadge}
                        ${rebootBadge}
                        ${revertBadge}
                        ${docsButton}
                    </div>
                    <p class="text-xs text-gray-400">${getSettingDescription(item.id, item.description)}</p>
                    ${variablesHTML}
                    ${codePreview}
                </div>
            </div>
            <div class="relative inline-block w-10 sm:mr-2 align-middle select-none transition duration-200 ease-in flex-shrink-0">
                <input type="checkbox" name="toggle" id="${item.id}" class="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer" onchange="window.app.updateCode()"/>
                <label for="${item.id}" class="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"></label>
            </div>
        </div>
    `;
}

/**
 * 大項目（カテゴリ）のHTMLを生成
 * @param category - カテゴリオブジェクト
 * @param categoryIndex - カテゴリのインデックス
 * @param createSectionHTML - セクションHTML生成関数
 * @returns HTML文字列
 */
export function createCategoryHTML(category: Category, categoryIndex: number, createSectionHTML: (section: Section, categoryId: string, categoryIndex: number, sectionIndex: number) => string): string {
    const categoryId = 'category_' + categoryIndex;
    const sectionsHTML = category.sections.map((section, sectionIndex) => 
        createSectionHTML(section, categoryId, categoryIndex, sectionIndex)
    ).join('');
    
    // カテゴリ内の選択数をカウント
    let categorySelectedCount = 0;
    category.sections.forEach(section => {
        section.items.forEach(item => {
            const checkbox = document.getElementById(item.id) as HTMLInputElement | null;
            if (checkbox && checkbox.checked) {
                categorySelectedCount++;
            }
        });
    });
    const categoryBadge = categorySelectedCount > 0 ? `<span class="text-xs bg-purple-600 text-white px-2 py-0.5 rounded ml-2">${categorySelectedCount}</span>` : '';
    
    return `
        <div class="mb-6 category-container" data-category-title="${category.title.toLowerCase()}">
            <button class="category-header w-full flex items-center justify-between bg-gray-700 hover:bg-gray-650 p-2 sm:p-3 md:p-4 rounded-lg border border-gray-600 hover:border-gray-400 transition cursor-pointer" onclick="window.app.toggleCategory('${categoryId}')">
                <div class="flex items-center flex-wrap gap-2">
                    <svg class="category-arrow w-4 md:w-5 h-4 md:h-5 text-purple-400 mr-2 md:mr-3 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                    <h1 class="text-purple-400 text-sm md:text-base font-bold uppercase tracking-wide">${getCategoryTitle(category.title, category.title)}</h1>
                    ${categoryBadge}
                </div>
            </button>
            <div class="category-content hidden mt-2" id="${categoryId}">
                ${sectionsHTML}
            </div>
        </div>
    `;
}

/**
 * 中項目（セクション）のHTMLを生成
 * @param section - セクションオブジェクト
 * @param categoryId - カテゴリID
 * @param categoryIndex - カテゴリのインデックス
 * @param sectionIndex - セクションのインデックス
 * @returns HTML文字列
 */
export function createSectionHTML(section: Section, categoryId: string, categoryIndex: number, sectionIndex: number): string {
    const itemsHTML = section.items.map(item => createToggleHTML(item)).join('');
    // カテゴリインデックスとセクションインデックスを含めて一意なセクションIDを生成
    const sectionId = categoryId + '_section_' + sectionIndex;
    // 選択されている項目数をカウント
    const selectedCount = section.items.filter(item => {
        const checkbox = document.getElementById(item.id) as HTMLInputElement | null;
        return checkbox && checkbox.checked;
    }).length;
    const countBadge = selectedCount > 0 ? `<span class="text-xs bg-blue-600 text-white px-2 py-0.5 rounded ml-2">${selectedCount}</span>` : '';
    
    return `
        <div class="mb-4 ml-4 section-container" data-section-title="${section.title.toLowerCase()}">
            <button class="section-header w-full flex items-center justify-between bg-gray-800 hover:bg-gray-750 p-2 rounded-lg border border-gray-700 hover:border-gray-500 transition cursor-pointer" onclick="window.app.toggleSection('${sectionId}')">
                <div class="flex items-center flex-wrap gap-2">
                    <svg class="section-arrow w-3 md:w-4 h-3 md:h-4 text-blue-400 mr-1 md:mr-2 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                    <h2 class="text-blue-400 text-xs md:text-sm font-bold uppercase tracking-wide">${getSectionTitle(section.title, section.title)}</h2>
                    ${countBadge}
                </div>
            </button>
            <div class="section-content hidden mt-2" id="${sectionId}">
                ${itemsHTML}
            </div>
        </div>
    `;
}

